import { useCallback, useEffect, useReducer, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import * as R from 'ramda';

import { SubItemTreeViewState } from '@atom/components/common/subItemTree/subItemTreeReducer';
import { GET_WORK_ORDER } from '@atom/graph/work';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { usePreviousValue } from '@atom/hooks/usePreviousValue';
import {
  INITIAL_WORK_VALIDATIONS,
  useWorkValidations,
} from '@atom/hooks/useWorkValidations';
import { Progress } from '@atom/mui';
import { Task } from '@atom/types/task';
import {
  WorkOrderDetailType,
  WorkOrderTab,
  WorkOrderType,
} from '@atom/types/work';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import WorkOrderDetailHeader from './workOrderDetailHeader/WorkOrderDetailHeader';
import WorkOrderDocuments from './workOrderDocuments/WorkOrderDocuments';
import WorkOrderInfo from './workOrderInfo/WorkOrderInfo';
import WorkOrderPhotos from './workOrderPhotos/WorkOrderPhotos';
import WorkOrderTasks from './workOrderTasks/WorkOrderTasks';
import WorkOrderTeam from './workOrderTeam/WorkOrderTeam';
import WorkOrderVideos from './workOrderVideos/WorkOrderVideos';
import WorkOrderContext, {
  reducer,
  WorkOrderActionTypes,
} from './WorkOrderContext';

import classes from './workOrderDetail.module.css';

const DEFAULT_TAB = WorkOrderTab.TASKS;

const WorkOrderDetail = () => {
  const preferences = usePreferences();

  const [{ view: viewParam, task: taskId }, { params, updateSearchParams }] =
    useNavigation({
      task: SearchParamType.STRING,
      view: SearchParamType.STRING,
    });

  const { workOrderId } = params;

  const { workValidations, setWorkValidations } = useWorkValidations();

  const [workOrderDetail, dispatch] = useReducer(
    reducer,
    {} as WorkOrderDetailType,
  );
  // @ts-ignore
  const [task, setTask] = useState<Task>({});
  const [activeWorkOrderAsset, setActiveWorkOrderAsset] = useState<string>('');
  const [assetTreeViewState, setAssetTreeViewStateById] = useState<
    Record<string, SubItemTreeViewState>
  >({});
  const previousActiveWorkOrderAsset = usePreviousValue(activeWorkOrderAsset);
  const [refetchMedia, setRefetchMedia] = useState<boolean>(false);

  useEffect(() => {
    // If the current work order does not match the work order with validation
    // errors in global state, then clear it.
    if (workOrderId !== workValidations.workOrderId) {
      setWorkValidations(INITIAL_WORK_VALIDATIONS);
    }

    setActiveWorkOrderAsset('');
    setAssetTreeViewStateById({});
  }, [workOrderId]);

  const setAssetTreeViewState = useCallback(
    (rootAssetId: string, viewState: SubItemTreeViewState) =>
      setAssetTreeViewStateById(previous => ({
        ...previous,
        [rootAssetId]: viewState,
      })),
    [],
  );

  const setActiveView = (view: WorkOrderTab) => {
    updateSearchParams({ view });
  };

  const setActiveTask = (activeTaskId: string) => {
    updateSearchParams({ task: activeTaskId });
  };

  const {
    data: workOrderData,
    loading,
    error,
    refetch,
  } = useQuery(GET_WORK_ORDER, {
    variables: {
      id: workOrderId,
      showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (workOrderData?.workOrder) {
      const mappedWorkOrder = {
        ...workOrderData?.workOrder,
        type: R.pathOr(
          WorkOrderType.DEFAULT,
          ['workOrder', 'type'],
          workOrderData,
        ),
      };

      dispatch({
        type: WorkOrderActionTypes.SET_WORK_ORDER,
        data: mappedWorkOrder,
      });

      setActiveTask(
        !R.isEmpty(taskId)
          ? taskId
          : R.isEmpty(mappedWorkOrder.tasks)
            ? ''
            : mappedWorkOrder.tasks[0].id,
      );
      setTask(R.isEmpty(task) ? mappedWorkOrder.tasks[0] : task);
    }
  }, [workOrderData]);

  const workOrderDetailLoading = error || !workOrderDetail.id;

  useEffect(() => {
    if (!R.isEmpty(taskId) && !R.isEmpty(workOrderDetail)) {
      setTask(R.find(item => item.id === taskId, workOrderDetail.tasks));
    }

    // Clear task when last taskId is removed from workOrderDetail
    if (
      R.isEmpty(taskId) &&
      !R.isEmpty(workOrderDetail) &&
      R.isEmpty(workOrderDetail.tasks)
    ) {
      setTask(null);
    }
  }, [taskId, workOrderDetail]);

  useEffect(() => {
    updateSearchParams({
      view:
        viewParam ||
        R.pathOr(
          DEFAULT_TAB,
          ['workOrders', 'defaultWorkOrderTab'],
          preferences,
        ),
      task: taskId || '',
    });
  }, []);

  useEffect(() => {
    if (R.isEmpty(activeWorkOrderAsset) && previousActiveWorkOrderAsset) {
      refetch();
    }
  }, [activeWorkOrderAsset, workOrderDetail.id]);

  const getActiveTab = () => {
    const tabs = {
      [WorkOrderTab.INFO]: <WorkOrderInfo />,
      [WorkOrderTab.TASKS]: <WorkOrderTasks />,
      [WorkOrderTab.TEAM]: <WorkOrderTeam />,
      [WorkOrderTab.DOCUMENTS]: <WorkOrderDocuments />,
      [WorkOrderTab.PHOTOS]: <WorkOrderPhotos />,
      [WorkOrderTab.VIDEOS]: <WorkOrderVideos />,
    };

    return tabs[viewParam || DEFAULT_TAB];
  };

  return (
    <WorkOrderContext.Provider
      value={{
        refetch,
        workOrderDetail,
        dispatch,
        loading,
        task,
        setActiveTask,
        activeWorkOrderAsset,
        setActiveWorkOrderAsset,
        assetTreeViewState,
        setAssetTreeViewState,
        refetchMedia,
        setRefetchMedia,
        setTask,
      }}
    >
      {workOrderDetailLoading ? (
        <Progress />
      ) : (
        <>
          <WorkOrderDetailHeader
            activeView={viewParam || DEFAULT_TAB}
            setActiveView={setActiveView}
          />
          <div className={classes.tab}>{getActiveTab()}</div>
        </>
      )}
    </WorkOrderContext.Provider>
  );
};

export default WorkOrderDetail;
